// Período da Visão Geral — #6493.
//
// Telas de gestão abrem no mês atual, com seletor; telas operacionais abrem no
// dia. O mês corrente é comparado com o mesmo ponto do mês anterior (no dia 27,
// contra 1 a 27 do mês passado), a mesma "comparação justa" da aba CEO.
//
// Datas em texto 'AAAA-MM-DD', no calendário local. Lógica pura.

export interface JanelaDatas {
  /** Primeiro dia, inclusive. */
  inicio: string;
  /** Último dia, inclusive. */
  fim: string;
  rotulo: string;
}

export interface PeriodoVisaoGeral extends JanelaDatas {
  tipo: 'dia' | 'mes';
  /** 'AAAA-MM' do mês do período. */
  mes: string;
  /** É o mês (ou o dia) de hoje. */
  corrente: boolean;
  /** Quanto do período já passou, de 0 a 1 (1 = fechado). */
  fracao: number;
  /** Janela com que comparar: o mês anterior, cortado no mesmo dia quando o mês é o corrente. */
  anterior: JanelaDatas;
}

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const dois = (n: number) => String(n).padStart(2, '0');
export const isoLocal = (d: Date) => `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`;
const ultimoDia = (ano: number, mes0: number) => new Date(ano, mes0 + 1, 0).getDate();
const rotuloMes = (ano: number, mes0: number) => `${MESES[mes0][0].toUpperCase()}${MESES[mes0].slice(1)} de ${ano}`;

/** 'AAAA-MM' → { ano, mes0 }, ou null se não for um mês válido. */
export function lerMes(texto: string | null | undefined): { ano: number; mes0: number } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(texto || '');
  if (!m) return null;
  const ano = Number(m[1]);
  const mes0 = Number(m[2]) - 1;
  if (mes0 < 0 || mes0 > 11 || ano < 2000) return null;
  return { ano, mes0 };
}

export const mesDe = (d: Date) => `${d.getFullYear()}-${dois(d.getMonth() + 1)}`;

/** O mês pedido (padrão: o de hoje). Mês no futuro volta para o mês de hoje. */
export function periodoDoMes(mesTexto: string | null | undefined, hoje: Date): PeriodoVisaoGeral {
  const pedido = lerMes(mesTexto);
  const atual = { ano: hoje.getFullYear(), mes0: hoje.getMonth() };
  const alvo = pedido && (pedido.ano * 12 + pedido.mes0) <= (atual.ano * 12 + atual.mes0) ? pedido : atual;
  const { ano, mes0 } = alvo;
  const corrente = ano === atual.ano && mes0 === atual.mes0;
  const total = ultimoDia(ano, mes0);
  const diaCorte = corrente ? hoje.getDate() : total;

  const antAno = mes0 === 0 ? ano - 1 : ano;
  const antMes0 = mes0 === 0 ? 11 : mes0 - 1;
  const antUltimo = ultimoDia(antAno, antMes0);
  // Só o mês corrente corta o anterior no mesmo dia; mês fechado compara com o anterior inteiro.
  const antFimDia = corrente ? Math.min(diaCorte, antUltimo) : antUltimo;
  const antCortado = antFimDia < antUltimo;

  return {
    tipo: 'mes',
    mes: `${ano}-${dois(mes0 + 1)}`,
    inicio: `${ano}-${dois(mes0 + 1)}-01`,
    fim: `${ano}-${dois(mes0 + 1)}-${dois(total)}`,
    rotulo: rotuloMes(ano, mes0),
    corrente,
    fracao: corrente ? diaCorte / total : 1,
    anterior: {
      inicio: `${antAno}-${dois(antMes0 + 1)}-01`,
      fim: `${antAno}-${dois(antMes0 + 1)}-${dois(antFimDia)}`,
      rotulo: antCortado ? `1 a ${antFimDia} de ${MESES[antMes0]}` : rotuloMes(antAno, antMes0),
    },
  };
}

/** Hoje, comparado com o mesmo dia da semana passada. */
export function periodoDoDia(hoje: Date): PeriodoVisaoGeral {
  const dia = isoLocal(hoje);
  const semanaPassada = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 7);
  const ant = isoLocal(semanaPassada);
  return {
    tipo: 'dia',
    mes: mesDe(hoje),
    inicio: dia,
    fim: dia,
    rotulo: `Hoje, ${dois(hoje.getDate())}/${dois(hoje.getMonth() + 1)}`,
    corrente: true,
    fracao: 1,
    anterior: { inicio: ant, fim: ant, rotulo: `${dois(semanaPassada.getDate())}/${dois(semanaPassada.getMonth() + 1)}` },
  };
}

/** Saudação pela hora local. */
export function saudacao(agora: Date): string {
  const h = agora.getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}
